"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { useAuth } from "@/contexts/AuthContext";
import { createClient } from "@/lib/supabase/client";
import Icon from "@/components/ui/AppIcon";
import { motion, AnimatePresence } from "framer-motion";

interface StrategyCallRequest {
  id: string;
  fullName: string;
  email: string;
  company: string;
  platform: string;
  monthlyVolume: string;
  message: string | null;
  ipAddress: string | null;
  status: string;
  createdAt: string;
}

interface Filters {
  platform: string;
  monthlyVolume: string;
  dateFrom: string;
  dateTo: string;
  status: string;
}

const platforms = ["All", "Shopify", "WooCommerce", "BigCommerce", "TikTok Shop", "Amazon", "Other"];
const orderRanges = ["All", "< 100 orders/month", "100–500/month", "500–2,000/month", "2,000–10,000/month", "10,000+/month"];
const statuses = ["All", "pending", "contacted", "completed"];

export default function AdminDashboard() {
  const router = useRouter();
  const { user, loading: authLoading } = useAuth();
  const [requests, setRequests] = useState<StrategyCallRequest[]>([]);
  const [filteredRequests, setFilteredRequests] = useState<StrategyCallRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState<Filters>({
    platform: "All",
    monthlyVolume: "All",
    dateFrom: "",
    dateTo: "",
    status: "All"
  });
  const [sortColumn, setSortColumn] = useState<keyof StrategyCallRequest>("createdAt");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [selectedRequest, setSelectedRequest] = useState<StrategyCallRequest | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Authentication check
  useEffect(() => {
    if (!authLoading && !user) {
      router.replace("/login");
    }
  }, [user, authLoading, router]);

  // Fetch strategy call requests
  useEffect(() => {
    if (!user) return;

    const fetchRequests = async () => {
      try {
        setLoading(true);
        setError(null);
        const supabase = createClient();
        
        const { data, error: fetchError } = await supabase
          .from("strategy_call_requests")
          .select("*")
          .order("created_at", { ascending: false });

        if (fetchError) {
          throw fetchError;
        }

        // Convert snake_case to camelCase
        const formattedData = data?.map((row: any) => ({
          id: row.id,
          fullName: row.full_name,
          email: row.email,
          company: row.company,
          platform: row.platform,
          monthlyVolume: row.monthly_volume,
          message: row.message,
          ipAddress: row.ip_address,
          status: row.status || "pending",
          createdAt: row.created_at
        })) || [];

        setRequests(formattedData);
        setFilteredRequests(formattedData);
      } catch (err: any) {
        console.error("Error fetching requests:", err);
        setError(err.message || "Failed to load requests");
      } finally {
        setLoading(false);
      }
    };

    fetchRequests();
  }, [user]);

  // Apply filters and search
  useEffect(() => {
    let filtered = [...requests];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (req) =>
          req.fullName.toLowerCase().includes(query) ||
          req.email.toLowerCase().includes(query) ||
          req.company.toLowerCase().includes(query)
      );
    }

    // Platform filter
    if (filters.platform !== "All") {
      filtered = filtered.filter((req) => req.platform === filters.platform);
    }

    // Monthly volume filter
    if (filters.monthlyVolume !== "All") {
      filtered = filtered.filter((req) => req.monthlyVolume === filters.monthlyVolume);
    }

    // Status filter
    if (filters.status !== "All") {
      filtered = filtered.filter((req) => req.status === filters.status);
    }

    // Date range filter
    if (filters.dateFrom) {
      filtered = filtered.filter((req) => new Date(req.createdAt) >= new Date(filters.dateFrom));
    }
    if (filters.dateTo) {
      filtered = filtered.filter((req) => new Date(req.createdAt) <= new Date(filters.dateTo));
    }

    // Sorting
    filtered.sort((a, b) => {
      const aValue = a[sortColumn];
      const bValue = b[sortColumn];
      
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;
      
      if (sortDirection === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredRequests(filtered);
  }, [requests, searchQuery, filters, sortColumn, sortDirection]);

  const handleSort = (column: keyof StrategyCallRequest) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const handleStatusUpdate = async (requestId: string, newStatus: string) => {
    try {
      setUpdatingStatus(true);
      const supabase = createClient();
      
      const { error: updateError } = await supabase
        .from("strategy_call_requests")
        .update({ status: newStatus })
        .eq("id", requestId);

      if (updateError) {
        throw updateError;
      }

      // Update local state
      setRequests((prev) =>
        prev.map((req) => (req.id === requestId ? { ...req, status: newStatus } : req))
      );

      if (selectedRequest?.id === requestId) {
        setSelectedRequest({ ...selectedRequest, status: newStatus });
      }
    } catch (err: any) {
      console.error("Error updating status:", err);
      alert("Failed to update status: " + err.message);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const openDetailsModal = (request: StrategyCallRequest) => {
    setSelectedRequest(request);
    setShowDetailsModal(true);
  };

  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedRequest(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "bg-yellow-500/20 text-yellow-400 border-yellow-500/30";
      case "contacted":
        return "bg-blue-500/20 text-blue-400 border-blue-500/30";
      case "completed":
        return "bg-green-500/20 text-green-400 border-green-500/30";
      default:
        return "bg-gray-500/20 text-gray-400 border-gray-500/30";
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-deep-dark flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-primary mx-auto mb-4"></div>
          <p className="text-text-secondary">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-deep-dark py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-text-primary mb-2">Admin Dashboard</h1>
          <p className="text-text-secondary">Manage strategy call requests</p>
        </div>

        {/* Filters Section */}
        <div className="glass-card-dark border border-border-dark rounded-xl p-6 mb-6">
          <h2 className="text-lg font-semibold text-text-primary mb-4">Filters & Search</h2>
          
          {/* Search */}
          <div className="mb-4">
            <div className="relative">
              <Icon name="MagnifyingGlassIcon" size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary" />
              <input
                type="text"
                placeholder="Search by name, email, or company..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-secondary-dark border border-border-dark rounded-lg text-text-primary placeholder-text-secondary focus:outline-none focus:border-accent-primary transition-colors"
              />
            </div>
          </div>

          {/* Filter Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">Platform</label>
              <select
                value={filters.platform}
                onChange={(e) => setFilters({ ...filters, platform: e.target.value })}
                className="w-full px-4 py-2 bg-secondary-dark border border-border-dark rounded-lg text-text-primary focus:outline-none focus:border-accent-primary transition-colors"
              >
                {platforms.map((platform) => (
                  <option key={platform} value={platform}>
                    {platform}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">Monthly Volume</label>
              <select
                value={filters.monthlyVolume}
                onChange={(e) => setFilters({ ...filters, monthlyVolume: e.target.value })}
                className="w-full px-4 py-2 bg-secondary-dark border border-border-dark rounded-lg text-text-primary focus:outline-none focus:border-accent-primary transition-colors"
              >
                {orderRanges.map((range) => (
                  <option key={range} value={range}>
                    {range}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">Status</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="w-full px-4 py-2 bg-secondary-dark border border-border-dark rounded-lg text-text-primary focus:outline-none focus:border-accent-primary transition-colors"
              >
                {statuses.map((status) => (
                  <option key={status} value={status}>
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">Date From</label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                className="w-full px-4 py-2 bg-secondary-dark border border-border-dark rounded-lg text-text-primary focus:outline-none focus:border-accent-primary transition-colors"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">Date To</label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                className="w-full px-4 py-2 bg-secondary-dark border border-border-dark rounded-lg text-text-primary focus:outline-none focus:border-accent-primary transition-colors"
              />
            </div>
          </div>

          {/* Clear Filters */}
          <button
            onClick={() => {
              setSearchQuery("");
              setFilters({
                platform: "All",
                monthlyVolume: "All",
                dateFrom: "",
                dateTo: "",
                status: "All"
              });
            }}
            className="mt-4 px-4 py-2 bg-secondary-dark border border-border-dark rounded-lg text-text-secondary hover:text-accent-primary hover:border-accent-primary transition-colors"
          >
            Clear All Filters
          </button>
        </div>

        {/* Results Count */}
        <div className="mb-4 text-text-secondary">
          Showing {filteredRequests.length} of {requests.length} requests
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400">
            {error}
          </div>
        )}

        {/* Data Table */}
        <div className="glass-card-dark border border-border-dark rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-secondary-dark border-b border-border-dark">
                <tr>
                  <th
                    onClick={() => handleSort("fullName")}
                    className="px-6 py-4 text-left text-sm font-semibold text-text-primary cursor-pointer hover:text-accent-primary transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      Name
                      {sortColumn === "fullName" && (
                        <Icon name={sortDirection === "asc" ? "ChevronUpIcon" : "ChevronDownIcon"} size={16} />
                      )}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort("email")}
                    className="px-6 py-4 text-left text-sm font-semibold text-text-primary cursor-pointer hover:text-accent-primary transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      Email
                      {sortColumn === "email" && (
                        <Icon name={sortDirection === "asc" ? "ChevronUpIcon" : "ChevronDownIcon"} size={16} />
                      )}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort("company")}
                    className="px-6 py-4 text-left text-sm font-semibold text-text-primary cursor-pointer hover:text-accent-primary transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      Company
                      {sortColumn === "company" && (
                        <Icon name={sortDirection === "asc" ? "ChevronUpIcon" : "ChevronDownIcon"} size={16} />
                      )}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort("platform")}
                    className="px-6 py-4 text-left text-sm font-semibold text-text-primary cursor-pointer hover:text-accent-primary transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      Platform
                      {sortColumn === "platform" && (
                        <Icon name={sortDirection === "asc" ? "ChevronUpIcon" : "ChevronDownIcon"} size={16} />
                      )}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort("status")}
                    className="px-6 py-4 text-left text-sm font-semibold text-text-primary cursor-pointer hover:text-accent-primary transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      Status
                      {sortColumn === "status" && (
                        <Icon name={sortDirection === "asc" ? "ChevronUpIcon" : "ChevronDownIcon"} size={16} />
                      )}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort("createdAt")}
                    className="px-6 py-4 text-left text-sm font-semibold text-text-primary cursor-pointer hover:text-accent-primary transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      Date
                      {sortColumn === "createdAt" && (
                        <Icon name={sortDirection === "asc" ? "ChevronUpIcon" : "ChevronDownIcon"} size={16} />
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-text-primary">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-dark">
                {filteredRequests.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-text-secondary">
                      No requests found
                    </td>
                  </tr>
                ) : (
                  filteredRequests.map((request) => (
                    <tr key={request.id} className="hover:bg-secondary-dark/50 transition-colors">
                      <td className="px-6 py-4 text-sm text-text-primary">{request.fullName}</td>
                      <td className="px-6 py-4 text-sm text-text-secondary">{request.email}</td>
                      <td className="px-6 py-4 text-sm text-text-secondary">{request.company}</td>
                      <td className="px-6 py-4 text-sm text-text-secondary">{request.platform}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(request.status)}`}>
                          {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-text-secondary">{formatDate(request.createdAt)}</td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => openDetailsModal(request)}
                          className="text-accent-primary hover:text-accent-secondary transition-colors font-medium text-sm"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Details Modal */}
      <AnimatePresence>
        {showDetailsModal && selectedRequest && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeDetailsModal}
          >
            <motion.div
              className="bg-secondary-dark border border-border-dark rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-border-dark">
                <h2 className="text-2xl font-bold text-text-primary">Request Details</h2>
                <button
                  onClick={closeDetailsModal}
                  className="p-2 hover:bg-deep-dark rounded-lg transition-colors"
                >
                  <Icon name="XMarkIcon" size={24} className="text-text-secondary" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6 space-y-6">
                {/* Status Management */}
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">Status</label>
                  <select
                    value={selectedRequest.status}
                    onChange={(e) => handleStatusUpdate(selectedRequest.id, e.target.value)}
                    disabled={updatingStatus}
                    className="w-full px-4 py-2 bg-deep-dark border border-border-dark rounded-lg text-text-primary focus:outline-none focus:border-accent-primary transition-colors disabled:opacity-50"
                  >
                    <option value="pending">Pending</option>
                    <option value="contacted">Contacted</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>

                {/* Contact Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Full Name</label>
                    <p className="text-text-primary font-medium">{selectedRequest.fullName}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Email</label>
                    <p className="text-text-primary font-medium">{selectedRequest.email}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Company</label>
                    <p className="text-text-primary font-medium">{selectedRequest.company}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Platform</label>
                    <p className="text-text-primary font-medium">{selectedRequest.platform}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Monthly Volume</label>
                    <p className="text-text-primary font-medium">{selectedRequest.monthlyVolume}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Submitted</label>
                    <p className="text-text-primary font-medium">{formatDate(selectedRequest.createdAt)}</p>
                  </div>
                </div>

                {/* Message */}
                {selectedRequest.message && (
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">Message</label>
                    <div className="p-4 bg-deep-dark border border-border-dark rounded-lg">
                      <p className="text-text-primary whitespace-pre-wrap">{selectedRequest.message}</p>
                    </div>
                  </div>
                )}

                {/* Technical Information */}
                <div className="pt-4 border-t border-border-dark">
                  <h3 className="text-sm font-medium text-text-secondary mb-3">Technical Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-text-secondary mb-1">Request ID</label>
                      <p className="text-sm text-text-primary font-mono">{selectedRequest.id}</p>
                    </div>
                    {selectedRequest.ipAddress && (
                      <div>
                        <label className="block text-xs text-text-secondary mb-1">IP Address</label>
                        <p className="text-sm text-text-primary font-mono">{selectedRequest.ipAddress}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex justify-end gap-3 p-6 border-t border-border-dark">
                <button
                  onClick={closeDetailsModal}
                  className="px-6 py-2 bg-deep-dark border border-border-dark rounded-lg text-text-primary hover:border-accent-primary transition-colors"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}